import { Suspense } from "react";

import { ErrorBoundary } from "@suspensive/react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import { ContestScreeningBadge } from "@/components/contest-submissions/contest-screening-badge";
import { ManuscriptViewer } from "@/components/contest-submissions/detail/manuscript-viewer";
import { SubmissionInfoSections } from "@/components/contest-submissions/detail/submission-info-sections";
import { SubmissionReviewPanel } from "@/components/contest-submissions/detail/submission-review-panel";
import { SectionErrorFallback } from "@/components/error-boundary/error-fallback";
import { TableLoadingFallback } from "@/components/suspense/loading-fallback";
import { Button } from "@/components/ui/button";
import { contestSubmissionDetailQueryOptions } from "@/hooks/use-contest-submissions";

function DetailBody({ submissionId }: { submissionId: string }) {
  const navigate = useNavigate();
  const { data: detail } = useSuspenseQuery(
    contestSubmissionDetailQueryOptions(submissionId),
  );

  if (!detail) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        제출 이력을 찾을 수 없습니다.
      </div>
    );
  }

  const handleBack = () => {
    // 목록에서 들어왔으면 필터/페이지가 살아있는 히스토리로 복귀, 직접 진입이면 목록으로.
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/contest-submissions");
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" onClick={handleBack} aria-label="목록으로">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="min-w-0 flex-1 truncate text-2xl font-bold tracking-tight">
          {detail.workTitle || "(제목 없음)"}
        </h1>
        <ContestScreeningBadge screening={detail.screening} />
      </div>
      <p className="text-sm text-muted-foreground">
        {detail.name ?? "-"}
        {detail.penName && ` (필명: ${detail.penName})`} · {detail.email ?? detail.userEmail}
      </p>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_400px]">
        <ManuscriptViewer
          submissionId={detail._id}
          manuscript={detail.manuscript}
        />
        <aside className="self-start xl:sticky xl:top-6">
          {/* key 리마운트 — 제출물 전환 시에만 편집 상태 리셋 (배경 refetch 보호) */}
          <SubmissionReviewPanel key={detail._id} detail={detail} />
        </aside>
      </div>

      <SubmissionInfoSections detail={detail} />
    </div>
  );
}

export default function ContestSubmissionDetailPage() {
  const { submissionId } = useParams<{ submissionId: string }>();
  if (!submissionId) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        잘못된 경로입니다.
      </div>
    );
  }
  return (
    <ErrorBoundary fallback={SectionErrorFallback}>
      <Suspense fallback={<TableLoadingFallback columnCount={1} />}>
        <DetailBody submissionId={submissionId} />
      </Suspense>
    </ErrorBoundary>
  );
}
